import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config/api';
import {
  CircleNotch,
  DotsSixVertical,
  User,
  CalendarBlank,
  CurrencyDollar,
  CheckCircle,
  Clock,
  PenNib,
  ClipboardText,
  ArrowRight,
  FunnelSimple,
  ChartBar,
  Lightning
} from '@phosphor-icons/react';


const STAGE_CONFIG = {
  draft:       { icon: Clock,        bg: 'bg-slate-50',   border: 'border-slate-300',  headerBg: 'bg-slate-100',  text: 'text-slate-600',  dot: 'bg-slate-400' },
  sent:        { icon: ArrowRight,   bg: 'bg-blue-50',    border: 'border-blue-300',   headerBg: 'bg-blue-100',   text: 'text-blue-700',   dot: 'bg-blue-500' },
  signed:      { icon: PenNib,       bg: 'bg-purple-50',  border: 'border-purple-300', headerBg: 'bg-purple-100', text: 'text-purple-700', dot: 'bg-purple-500' },
  paid:        { icon: CurrencyDollar, bg: 'bg-amber-50', border: 'border-amber-300',  headerBg: 'bg-amber-100',  text: 'text-amber-700',  dot: 'bg-amber-500' },
  in_progress: { icon: Lightning,    bg: 'bg-red-50',     border: 'border-red-300',    headerBg: 'bg-red-100',    text: 'text-red-700',    dot: 'bg-red-500' },
  completed:   { icon: CheckCircle,  bg: 'bg-green-50',   border: 'border-green-300',  headerBg: 'bg-green-100',  text: 'text-green-700',  dot: 'bg-green-500' },
};

function PipelineCard({ card, onDragStart }) {
  const hasWO = !!card.work_order;
  const woProgress = hasWO && card.work_order.checklist_total > 0
    ? Math.round((card.work_order.checklist_done / card.work_order.checklist_total) * 100)
    : 0;

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, card.id)}
      className="bg-white rounded-xl border border-slate-200 p-3.5 cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow group"
      data-testid={`pipeline-card-${card.id}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <span className="text-xs font-bold text-slate-400">{card.quote_number}</span>
          <h4 className="font-semibold text-sm text-slate-900 truncate mt-0.5">{card.client_name}</h4>
        </div>
        <DotsSixVertical size={16} className="text-slate-300 group-hover:text-slate-500 shrink-0 mt-1" />
      </div>

      {/* Amount */}
      {card.total > 0 && (
        <p className="text-base font-bold text-slate-900 mb-2">
          ${card.total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </p>
      )}

      {/* Meta info */}
      <div className="flex flex-wrap gap-2 text-xs text-slate-500 mb-2">
        <span className="flex items-center gap-1">
          <CalendarBlank size={12} />
          {new Date(card.created_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
        </span>
        {card.items_count > 0 && (
          <span>{card.items_count} items</span>
        )}
        {card.auto_flow && (
          <span className="px-1.5 py-0.5 bg-purple-100 text-purple-600 rounded font-semibold">Auto</span>
        )}
      </div>

      {/* Signature info */}
      {card.signed_at && (
        <div className="flex items-center gap-1 text-xs text-purple-600 mb-2">
          <PenNib size={12} weight="fill" />
          <span>Firmada: {card.signature_name}</span>
        </div>
      )}

      {/* Invoice */}
      {card.auto_invoice_number && (
        <div className="text-xs text-amber-600 mb-2">
          Factura: {card.auto_invoice_number}
        </div>
      )}

      {/* Work Order info */}
      {hasWO && (
        <div className="mt-2 pt-2 border-t border-slate-100">
          <div className="flex items-center justify-between mb-1">
            <span className="flex items-center gap-1 text-xs font-medium text-slate-600">
              <ClipboardText size={12} />
              {card.work_order.work_order_number}
            </span>
            {card.work_order.scheduled_date && (
              <span className="text-xs text-slate-400">{card.work_order.scheduled_date}</span>
            )}
          </div>
          {card.work_order.checklist_total > 0 && (
            <div className="mt-1.5">
              <div className="flex items-center justify-between text-xs mb-0.5">
                <span className="text-slate-400">Progreso</span>
                <span className="font-semibold text-slate-600">{card.work_order.checklist_done}/{card.work_order.checklist_total}</span>
              </div>
              <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500 rounded-full transition-all"
                  style={{ width: `${woProgress}%` }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Link */}
      <Link
        to={card.type === 'work_order' ? `/work-orders/${card.work_order?.id || card.id}` : `/quotes/${card.id}`}
        className="block mt-2.5 text-center text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors"
      >
        Ver detalles
      </Link>
    </div>
  );
}

function PipelineColumn({ stage, cards, onDragStart, onDragOver, onDrop }) {
  const config = STAGE_CONFIG[stage.id] || STAGE_CONFIG.draft;
  const Icon = config.icon;
  const stageTotal = cards.reduce((sum, c) => sum + (c.total || 0), 0);

  return (
    <div
      className={`flex flex-col rounded-xl border ${config.border} ${config.bg} min-w-[280px] w-[280px] shrink-0 lg:min-w-0 lg:w-auto lg:flex-1`}
      onDragOver={onDragOver}
      onDrop={(e) => onDrop(e, stage.id)}
      data-testid={`pipeline-column-${stage.id}`}
    >
      {/* Column Header */}
      <div className={`${config.headerBg} rounded-t-xl px-3 py-2.5 border-b ${config.border}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`w-2.5 h-2.5 rounded-full ${config.dot}`} />
            <h3 className={`font-semibold text-sm ${config.text}`}>{stage.label}</h3>
          </div>
          <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${config.text} ${config.headerBg}`}>
            {cards.length}
          </span>
        </div>
        {stageTotal > 0 && (
          <p className={`text-xs mt-1 ${config.text} opacity-75`}>
            ${stageTotal.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </p>
        )}
      </div>

      {/* Cards */}
      <div className="flex-1 p-2 space-y-2 overflow-y-auto max-h-[calc(100vh-280px)] min-h-[120px]">
        {cards.length === 0 ? (
          <div className="text-center py-8 text-xs text-slate-400">
            Sin proyectos
          </div>
        ) : (
          cards.map(card => (
            <PipelineCard key={card.id} card={card} onDragStart={onDragStart} />
          ))
        )}
      </div>
    </div>
  );
}

export default function PipelinePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draggedId, setDraggedId] = useState(null);

  useEffect(() => {
    fetchPipeline();
  }, []);

  const fetchPipeline = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/pipeline`, { withCredentials: true });
      setData(res.data);
    } catch (error) {
      console.error('Error fetching pipeline:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (e, cardId) => {
    setDraggedId(cardId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetStage) => {
    e.preventDefault();
    if (!draggedId) return;

    // Only allow moving to draft, sent, signed, paid
    const movableStages = ['draft', 'sent', 'signed', 'paid'];
    if (!movableStages.includes(targetStage)) {
      setDraggedId(null);
      return;
    }

    try {
      await axios.put(`${API_URL}/api/pipeline/${draggedId}/move`, { stage: targetStage }, { withCredentials: true });
      fetchPipeline();
    } catch (error) {
      console.error('Error moving card:', error);
    }
    setDraggedId(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <CircleNotch size={40} className="animate-spin text-blue-600" />
      </div>
    );
  }

  const stats = data?.stats || {};
  const stages = data?.stages || [];
  const pipeline = data?.pipeline || {};

  return (
    <div className="space-y-5 animate-fadeIn" data-testid="pipeline-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2">
            <FunnelSimple size={26} className="text-blue-600" />
            Pipeline de Proyectos
          </h1>
          <p className="text-sm text-slate-500 mt-1">Vista Kanban de todas las cotizaciones y proyectos</p>
        </div>
        <Link
          to="/quotes/new"
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
          data-testid="new-quote-from-pipeline"
        >
          + Nueva Cotizacion
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-500 uppercase font-medium">Total Proyectos</p>
          <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="stat-total-projects">{stats.total_projects || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-500 uppercase font-medium">Valor Total</p>
          <p className="text-2xl font-bold text-blue-600 mt-1" data-testid="stat-total-value">
            ${(stats.total_value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-500 uppercase font-medium">Valor Cerrado</p>
          <p className="text-2xl font-bold text-green-600 mt-1" data-testid="stat-signed-value">
            ${(stats.signed_value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-500 uppercase font-medium">Tasa Conversion</p>
          <p className="text-2xl font-bold text-purple-600 mt-1" data-testid="stat-conversion-rate">
            {stats.conversion_rate || 0}%
          </p>
        </div>
      </div>

      {/* Kanban Board */}
      <div className="flex gap-3 overflow-x-auto pb-4 -mx-3 px-3 lg:mx-0 lg:px-0" data-testid="kanban-board">
        {stages.map(stage => (
          <PipelineColumn
            key={stage.id}
            stage={stage}
            cards={pipeline[stage.id] || []}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          />
        ))}
      </div>

      {/* Legend */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2">Flujo del Proyecto</h4>
        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
          {stages.map((stage, i) => {
            const config = STAGE_CONFIG[stage.id];
            return (
              <React.Fragment key={stage.id}>
                <span className="flex items-center gap-1">
                  <span className={`w-2 h-2 rounded-full ${config?.dot}`} />
                  {stage.label}
                </span>
                {i < stages.length - 1 && <ArrowRight size={12} className="text-slate-300" />}
              </React.Fragment>
            );
          })}
        </div>
        <p className="text-xs text-slate-400 mt-2">
          Arrastra las tarjetas entre columnas para actualizar el estado. Las columnas "En Progreso" y "Completada" se actualizan automaticamente desde las Ordenes de Trabajo.
        </p>
      </div>
    </div>
  );
}
